import React, { useEffect, useState } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import apiClient from '../../../api/apiClient';
import styles from './SurveySearchPage.module.css';
import { buildQueryString, getExportFilename } from './surveySearchHelpers';
import SurveyResults from './SurveyResults';
import FilterSection from './FilterSection';
import ConfirmDialog from '../../../components/Dialog';
import Banner from '../../../components/Banner';

const client = apiClient('survey');

function SurveySearchPage() {
  const [queryParams, setQueryParams] = useState({
    t: [],
    lfdn: [],
    participant: [],
    keyValues: {},
    page: 1,
    createdAtStart: '',
    createdAtEnd: '',
    uploadedAtStart: '',
    uploadedAtEnd: '',
    page_size: 20,
    only_collisions: false, // <-- add only_collisions to state
  });
  const [surveyData, setSurveyData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [keyValuePairs, setKeyValuePairs] = useState([
    { key: '', relation: '=', values: '' },
  ]);
  const [tInput, setTInput] = useState('');
  const [lfdnInput, setLfdnInput] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
  const [pageSize, setPageSize] = useState(20); // UI state for page size
  const [onlyCollisions, setOnlyCollisions] = useState(false);
  const [backendSortBy, setBackendSortBy] = useState(null);
  const [backendSortOrder, setBackendSortOrder] = useState('asc');
  const [variablesMapping, setVariablesMapping] = useState({});
  const [banner, setBanner] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [error, setError] = useState(false);
  const [completedSurveysInput, setCompletedSurveysInput] = useState(''); // <-- added
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch variables mapping on mount
  useEffect(() => {
    apiClient('survey')
      .get('/surveys/manage/variables')
      .then((res) => setVariablesMapping(res.data || {}))
      .catch(() => setVariablesMapping({}));
  }, []);

  // Fetch surveys when page changes (but not other params)
  useEffect(() => {
    if (hasSearched) {
      fetchSurveys();
    }
    // eslint-disable-next-line
  }, [queryParams.page, queryParams.page_size]);

  // Refetch surveys when onlyCollisions changes
  useEffect(() => {
    if (onlyCollisions) {
      setBackendSortBy('participant');
      setBackendSortOrder('asc');
    } else {
      setBackendSortBy(null);
      setBackendSortOrder('asc');
    }
    if (hasSearched) {
      fetchSurveys();
    }
    // eslint-disable-next-line
  }, [onlyCollisions]);

  useEffect(() => {
    if (hasSearched && totalPages > 1) {
      fetchSurveys();
    }
    // eslint-disable-next-line
  }, [backendSortBy, backendSortOrder]);

  const fetchSurveys = () => {
    setLoading(true);
    setError(false);
    let sortParams = '';
    if (backendSortBy && backendSortOrder) {
      sortParams = `&sort_by=${encodeURIComponent(backendSortBy)}&sort_order=${encodeURIComponent(backendSortOrder)}`;
    }
    const url = `surveys/list/query?${buildQueryString(queryParams)}${sortParams}`;
    client
      .get(url)
      .then((response) => {
        setSurveyData(response.data.surveys);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
        setError(false);
      })
      .catch(() => {
        setSurveyData([]);
        setTotalPages(1);
        setError(true);
        setBanner({
          open: true,
          message:
            error.response && error.response.statusText
              ? `Something went wrong, failed to fetch surveys: ${error.response.statusText}`
              : 'Something went wrong, failed to fetch surveys. Please modify filters and try again.',
          severity: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Validation helpers
  const validateCommaSeparated = (input, min, max) => {
    if (!input.trim()) return true;
    // Invalid if two commas follow without value in between
    if (input.includes(',,') || /^,|,$/.test(input)) return false;
    const parts = input.split(',').map((v) => v.trim());
    if (parts.some((v) => v === '')) return false;
    return parts.every((v) => {
      const num = Number(v);
      return Number.isInteger(num) && num >= min && num <= max;
    });
  };

  const validateTInput = (input) => validateCommaSeparated(input, 1, 8);
  const validateLfdnInput = (input) =>
    validateCommaSeparated(input, 1, Number.MAX_SAFE_INTEGER);
  const validateCompletedSurveysInput = (input) =>
    validateCommaSeparated(input, 1, 8);

  const handleSearch = () => {
    setBanner({ open: false, message: '', severity: 'error' });

    if (!validateTInput(tInput)) {
      setBanner({
        open: true,
        message:
          'Invalid T values. Use comma-separated integers between 1 and 8, no empty values.',
        severity: 'error',
      });
      return;
    }
    if (!validateLfdnInput(lfdnInput)) {
      setBanner({
        open: true,
        message:
          'Invalid LFDN values. Use comma-separated positive integers, no empty values.',
        severity: 'error',
      });
      return;
    }
    if (!validateCompletedSurveysInput(completedSurveysInput)) {
      setBanner({
        open: true,
        message:
          'Invalid Completed Surveys. Use comma-separated integers between 1 and 8, no empty values.',
        severity: 'error',
      });
      return;
    }
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      page_size: pageSize,
    }));
    setHasSearched(true);
    fetchSurveys();
  };

  const handlePageChange = (newPage) =>
    setQueryParams((prev) => ({ ...prev, page: newPage }));

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      page_size: newSize,
    }));
  };

  const clearFilters = () => {
    setQueryParams({
      t: [],
      lfdn: [],
      participant: [],
      keyValues: {},
      page: 1,
      createdAtStart: '',
      createdAtEnd: '',
      uploadedAtStart: '',
      uploadedAtEnd: '',
      page_size: 20,
      only_collisions: false,
      completed_surveys: [],
    });
    setOnlyCollisions(false);
    setPageSize(20);
    setKeyValuePairs([{ key: '', relation: '=', values: '' }]);
    setParticipantSearch('');
    setShowParticipantDropdown(false);
    setTInput('');
    setLfdnInput('');
    setCompletedSurveysInput('');
    setHasSearched(false); // Hide results after clearing filters
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const url = `surveys/list/query?${buildQueryString(queryParams)}&export=true`;
      const response = await client.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = getExportFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Export failed.');
      console.error(err);
    }
    setExporting(false);
  };

  const handleDeleteSelected = (ids) => {
    setPendingDeleteIds(ids);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSelected = async () => {
    if (!pendingDeleteIds || pendingDeleteIds.length === 0) {
      setDeleteDialogOpen(false);
      return;
    }
    try {
      const params = pendingDeleteIds
        .map((id) => `ids=${encodeURIComponent(id)}`)
        .join('&');
      await client.delete(`/surveys/delete-by-ids?${params}`);
      setDeleteDialogOpen(false);
      setPendingDeleteIds([]);
      fetchSurveys();
      setBanner({
        open: true,
        message: 'Selected surveys deleted successfully.',
        severity: 'success',
      });
    } catch (err) {
      setBanner({
        open: true,
        message: 'Failed to delete selected surveys.',
        severity: 'error',
      });
      setDeleteDialogOpen(false);
      setPendingDeleteIds([]);
    }
  };

  const cancelDeleteSelected = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteIds([]);
  };

  // Backend sort change handler
  const handleSortChange = (sortBy, sortOrder) => {
    setBackendSortBy(sortBy);
    setBackendSortOrder(sortOrder);
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
    }));
    // fetchSurveys will be triggered by useEffect on queryParams.page
  };

  return (
    <MainLayout>
      <div className={styles.container}>
        <h2>Manage Surveys</h2>
        <FilterSection
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          tInput={tInput}
          setTInput={setTInput}
          lfdnInput={lfdnInput}
          setLfdnInput={setLfdnInput}
          participantSearch={participantSearch}
          setParticipantSearch={setParticipantSearch}
          showParticipantDropdown={showParticipantDropdown}
          setShowParticipantDropdown={setShowParticipantDropdown}
          keyValuePairs={keyValuePairs}
          setKeyValuePairs={setKeyValuePairs}
          onlyCollisions={onlyCollisions}
          setOnlyCollisions={setOnlyCollisions}
          variablesMapping={variablesMapping}
          setBanner={setBanner}
          completedSurveysInput={completedSurveysInput} // <-- pass down
          setCompletedSurveysInput={setCompletedSurveysInput} // <-- pass down
        />
        <div className={styles.actionButtons}>
          <button onClick={handleSearch} className={styles.searchBtn}>
            Search
          </button>
          <button onClick={clearFilters} className={styles.clearBtn}>
            Clear Filters
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className={styles.exportBtn}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
        {hasSearched && (
          <SurveyResults
            loading={loading}
            surveyData={surveyData}
            totalCount={totalCount}
            totalPages={totalPages}
            page={queryParams.page}
            onPageChange={handlePageChange}
            onDeleteSelected={handleDeleteSelected}
            onRefresh={fetchSurveys}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onlyCollisions={onlyCollisions}
            onSortChange={handleSortChange}
            backendSortBy={backendSortBy}
            backendSortOrder={backendSortOrder}
            variablesMapping={variablesMapping}
            error={error}
          />
        )}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Confirm Deletion"
          content={`Are you sure you want to delete ${pendingDeleteIds.length} selected survey${
            pendingDeleteIds.length !== 1 ? 's' : ''
          }? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDeleteSelected}
          onCancel={cancelDeleteSelected}
        />
        <Banner
          open={banner.open}
          message={banner.message}
          severity={banner.severity}
          onClose={() => setBanner({ ...banner, open: false })}
        />
      </div>
    </MainLayout>
  );
}

export default SurveySearchPage;
